import { Fragment, type ReactNode, useEffect, useRef, useState } from "react";
import {
  ApiError,
  api,
  iniciarEnfoque,
  terminarEnfoque,
  type PrefsNotif,
  type PushTestOut,
} from "@/lib/api";
import { useCommitOnBlur } from "@/lib/useCommitOnBlur";
import { activarWebPush, estadoPush, type EstadoPush } from "@/lib/push";
import {
  CANALES_EXTERNOS,
  ENFOQUE_OPCIONES,
  NIVEL_ICONO,
  ORDEN_URGENCIA,
  TIPO_META,
  ENFOQUE_TEXTOS,
  URGENCIA_META,
  canalesDeNivel,
  formatHoraEnfoque,
  nivelDeTipo,
  type CanalExterno,
  type NivelUrgencia,
} from "@/lib/notifMeta";
import MisEquipos from "./MisEquipos";
import Toggle from "@/ui/Toggle";
import { useToast } from "@/ui/Toast";

/**
 * Centro de notificaciones del usuario. Arriba, los CANALES por los que puede
 * recibir avisos (la campanita in-app siempre activa; navegador/Telegram/correo
 * apagables). Luego el RUTEO por urgencia (qué tan fuerte suena cada aviso) y,
 * en modo avanzado, la urgencia por tipo de aviso. Abajo, la gestión del push
 * de ESTE navegador (alta por dispositivo + prueba) y el horario de silencio.
 * Nació del diagnóstico de la Fase 0.5 (push), se unificó como hub de canales
 * el 2026-06-30 y sumó el ruteo por urgencia (Task 10, F2).
 *
 * Sin tooltips: cada canal dice en su descripción qué pasa al apagarlo; la
 * rejilla, las fichas y los botones los explica la Ayuda de Mi perfil
 * (`components/ayuda/vistas/perfil.tsx`). Los switches sin texto visible
 * llevan su nombre para el lector de pantalla con un `sr-only` de hijo.
 */

/** Una fila de canal: ícono + nombre + descripción a la izquierda, control a
 *  la derecha. Apila suave en pantallas chicas. */
function CanalRow({
  icon,
  titulo,
  desc,
  control,
}: {
  icon: string;
  titulo: string;
  desc: string;
  control: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm text-zinc-200">
          <span aria-hidden>{icon}</span>
          <span>{titulo}</span>
        </div>
        <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{desc}</p>
      </div>
      <div className="shrink-0">{control}</div>
    </div>
  );
}

/** Selector segmentado 🔴/🟡/⚪ compacto (urgencia por tipo, sección avanzada). */
function SelectorNivel({
  value,
  onChange,
}: {
  value: NivelUrgencia;
  onChange: (nivel: NivelUrgencia) => void;
}) {
  return (
    <div className="inline-flex rounded-lg border border-ink-700 overflow-hidden shrink-0">
      {ORDEN_URGENCIA.map((n) => {
        const activo = value === n;
        return (
          <button
            key={n}
            type="button"
            aria-pressed={activo}
            aria-label={URGENCIA_META[n].titulo}
            onClick={() => onChange(n)}
            className={`px-2.5 py-1 text-sm leading-none transition ${
              activo ? "bg-ink-700" : "hover:bg-ink-800 opacity-60"
            }`}
          >
            {NIVEL_ICONO[n]}
          </button>
        );
      })}
    </div>
  );
}

export default function NotificacionesPanel() {
  const toast = useToast();
  const [estado, setEstado] = useState<EstadoPush | null>(null);
  const [activando, setActivando] = useState(false);
  const [probando, setProbando] = useState(false);
  const [prefs, setPrefs] = useState<PrefsNotif | null>(null);
  const [reloadEquipos, setReloadEquipos] = useState(0);
  const [enfoqueCargando, setEnfoqueCargando] = useState(false);
  // "Urgencia por tipo" es avanzado: empieza colapsado (mismo patrón que
  // "Seguimiento RMA" en PanelHitos).
  const [tipoAbierto, setTipoAbierto] = useState(false);
  // `prefsRef` espeja `prefs` de forma SÍNCRONA (se actualiza junto con cada
  // `setPrefs`, no vía useEffect) para que dos clics rápidos sobre el mismo
  // dict-columna (notif_nivel_canales / notif_tipo_urgencia) partan siempre
  // del valor más fresco, aunque React todavía no haya vuelto a renderizar.
  // `nivelQueueRef`/`tipoQueueRef` encolan los PATCH de cada campo en orden
  // (promise-chain) para que no lleguen fuera de secuencia al backend, que
  // REEMPLAZA el dict completo en vez de mezclarlo — sin esto, un PATCH viejo
  // que llegue tarde pisaría a uno más nuevo aunque el cliente ya calculó
  // bien el merge (lost update, hallazgo de code review).
  const prefsRef = useRef<PrefsNotif | null>(null);
  const nivelQueueRef = useRef<Promise<void>>(Promise.resolve());
  const tipoQueueRef = useRef<Promise<void>>(Promise.resolve());

  function aplicarPrefs(p: PrefsNotif) {
    prefsRef.current = p;
    setPrefs(p);
  }

  /** Reconcilia con el backend (GET) tras un error, para no dejar la UI
   *  mintiendo con un update optimista que nunca se guardó. */
  async function recargarPrefs() {
    try {
      aplicarPrefs(
        await api.get<PrefsNotif>("/api/users/me/notificaciones"),
      );
    } catch {
      // si ni el GET jala, dejamos el estado optimista tal cual; el próximo
      // refresh de la página lo corrige.
    }
  }

  async function refrescar() {
    setEstado(await estadoPush());
  }

  useEffect(() => {
    void refrescar();
    api
      .get<PrefsNotif>("/api/users/me/notificaciones")
      .then(aplicarPrefs)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const onChange = () => void recargarPrefs();
    window.addEventListener("enfoque:changed", onChange);
    return () => window.removeEventListener("enfoque:changed", onChange);
  }, []);

  async function cambiarPrefs(parcial: Partial<PrefsNotif>) {
    try {
      aplicarPrefs(
        await api.patch<PrefsNotif>("/api/users/me/notificaciones", parcial),
      );
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude guardar la preferencia", msg);
    }
  }

  // Horario de silencio: editar en local y guardar al SALIR del campo, no en
  // cada tic del selector de hora (mismo patrón que las fechas del caso).
  const silencioInicio = useCommitOnBlur(prefs?.silencio_inicio ?? "", (v) => {
    void cambiarPrefs({ silencio_inicio: v });
  });
  const silencioFin = useCommitOnBlur(prefs?.silencio_fin ?? "", (v) => {
    void cambiarPrefs({ silencio_fin: v });
  });

  /** Prende/apaga un canal externo (push/Telegram/correo) con guardado
   *  instantáneo y su propio toast. */
  async function cambiarCanal(
    campo: "notif_push" | "notif_telegram" | "notif_email",
    valor: boolean,
    nombre: string,
    detalleOff?: string,
  ) {
    try {
      aplicarPrefs(
        await api.patch<PrefsNotif>("/api/users/me/notificaciones", {
          [campo]: valor,
        }),
      );
      toast.success(
        valor ? `${nombre}: activado` : `${nombre}: silenciado`,
        valor ? undefined : detalleOff,
      );
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude guardar la preferencia", msg);
    }
  }

  /** Modo enfoque (Task 12): inicia el silencio de push/Telegram por
   *  `minutos` (la campanita sigue recibiendo todo). Dispara
   *  "enfoque:changed" para que el botón del Layout se refresque solo. */
  async function iniciarEnf(minutos: number) {
    setEnfoqueCargando(true);
    try {
      aplicarPrefs(await iniciarEnfoque(minutos));
      window.dispatchEvent(new Event("enfoque:changed"));
      toast.success(
        "Modo enfoque activado",
        "Push/Telegram en silencio; la campanita sigue recibiendo todo.",
      );
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo activar el modo enfoque", msg);
    } finally {
      setEnfoqueCargando(false);
    }
  }

  /** Termina el modo enfoque antes de tiempo. */
  async function terminarEnf() {
    setEnfoqueCargando(true);
    try {
      aplicarPrefs(await terminarEnfoque());
      window.dispatchEvent(new Event("enfoque:changed"));
      toast.success("Modo enfoque terminado");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo terminar el modo enfoque", msg);
    } finally {
      setEnfoqueCargando(false);
    }
  }

  /** Prende/apaga un canal externo (push/Telegram) para UN nivel de urgencia.
   *  Manda el diccionario COMPLETO (el backend reemplaza, no mezcla) partiendo
   *  de lo ya guardado + el nivel tocado — así no se pierden overrides de
   *  otros niveles.
   *
   *  Optimista y acumulativo: el merge parte de `prefsRef` (siempre al día,
   *  no del `prefs` capturado por el closure) para que dos clics rápidos
   *  sobre el MISMO nivel se sumen en vez de pisarse — el 2º clic ya ve el
   *  cambio optimista del 1º aunque React no haya re-renderizado. El PATCH
   *  real se encola en `nivelQueueRef` para que llegue en orden al backend
   *  (evita que un request viejo, si se retrasa en la red, aterrice después
   *  del nuevo y lo pise). */
  function cambiarNivelCanal(
    nivel: NivelUrgencia,
    canal: CanalExterno,
    valor: boolean,
  ) {
    const base = prefsRef.current;
    if (!base) return;
    const actual = canalesDeNivel(nivel, base.notif_nivel_canales);
    const set = new Set<CanalExterno>(actual);
    if (valor) set.add(canal);
    else set.delete(canal);
    const nuevo = CANALES_EXTERNOS.filter((c) => set.has(c));
    const notifNivelCanales = { ...base.notif_nivel_canales, [nivel]: nuevo };
    aplicarPrefs({ ...base, notif_nivel_canales: notifNivelCanales });

    const nombreCanal = canal === "push" ? "navegador" : "Telegram";
    nivelQueueRef.current = nivelQueueRef.current.then(async () => {
      try {
        await api.patch<PrefsNotif>("/api/users/me/notificaciones", {
          notif_nivel_canales: notifNivelCanales,
        });
        toast.success(
          `${URGENCIA_META[nivel].titulo}: ${nombreCanal} ${valor ? "activado" : "apagado"}`,
        );
      } catch (err) {
        await recargarPrefs();
        const msg = err instanceof ApiError ? err.detail : "Error";
        toast.error("No pude guardar la preferencia", msg);
      }
    });
  }

  /** Cambia el nivel de urgencia de UN tipo de aviso (sección avanzada).
   *  Mismo patrón optimista + encolado que `cambiarNivelCanal` (evita el
   *  lost-update de dos tipos cambiados con clics rápidos seguidos). */
  function cambiarTipoUrgencia(tipo: string, nivel: NivelUrgencia) {
    const base = prefsRef.current;
    if (!base) return;
    const notifTipoUrgencia = { ...base.notif_tipo_urgencia, [tipo]: nivel };
    aplicarPrefs({ ...base, notif_tipo_urgencia: notifTipoUrgencia });

    tipoQueueRef.current = tipoQueueRef.current.then(async () => {
      try {
        await api.patch<PrefsNotif>("/api/users/me/notificaciones", {
          notif_tipo_urgencia: notifTipoUrgencia,
        });
        toast.success(`${TIPO_META[tipo]?.label ?? tipo}: ${URGENCIA_META[nivel].titulo}`);
      } catch (err) {
        await recargarPrefs();
        const msg = err instanceof ApiError ? err.detail : "Error";
        toast.error("No pude guardar la preferencia", msg);
      }
    });
  }

  async function activar() {
    setActivando(true);
    try {
      const permiso = await Notification.requestPermission();
      if (permiso === "denied") {
        toast.error(
          "El navegador tiene bloqueadas las notificaciones",
          "Desbloquéalas desde el candado junto a la dirección del sitio y vuelve a intentar.",
        );
        return;
      }
      if (permiso !== "granted") return; // cerró el aviso sin decidir
      await activarWebPush();
      const e = await estadoPush();
      setEstado(e);
      setReloadEquipos((n) => n + 1);
      if (e.suscrito) {
        toast.success("Notificaciones activadas en este dispositivo");
      } else {
        toast.error(
          "No se pudo dar de alta este dispositivo",
          "Vuelve a intentar; si persiste, avisa al administrador.",
        );
      }
    } finally {
      setActivando(false);
      void refrescar();
    }
  }

  async function probar() {
    setProbando(true);
    try {
      const r = await api.post<PushTestOut>("/api/push/test", {});
      if (r.total === 0) {
        toast.error(
          "No tienes dispositivos suscritos",
          "Activa las notificaciones primero con el botón de al lado.",
        );
      } else if (r.enviados === r.total) {
        toast.success(
          `Prueba enviada a ${r.total} dispositivo${r.total === 1 ? "" : "s"}`,
          "Revisa que la notificación haya aparecido en cada uno.",
        );
      } else {
        toast.success(
          `Prueba enviada a ${r.enviados} de ${r.total} dispositivos`,
          "Los que ya no existían se dieron de baja automáticamente.",
        );
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude enviar la prueba", msg);
    } finally {
      setProbando(false);
      setReloadEquipos((n) => n + 1);
    }
  }

  const PERMISO_PILL: Record<string, [string, string]> = {
    granted: ["permitido", "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"],
    denied: ["bloqueado", "bg-red-500/10 text-red-300 border-red-500/30"],
    default: ["sin pedir", "bg-zinc-500/10 text-zinc-400 border-zinc-500/30"],
  };
  const [permisoLabel, permisoCls] =
    PERMISO_PILL[estado?.permiso ?? "default"] ?? PERMISO_PILL.default;
  const suscrito = estado?.suscrito ?? false;
  const pushSoportado = estado?.soportado ?? true;
  const enfoqueActivo =
    !!prefs?.enfoque_hasta && new Date(prefs.enfoque_hasta) > new Date();

  return (
    <div className="card space-y-3">
      <div>
        <h2 className="text-sm font-medium text-zinc-300">Notificaciones</h2>
        <p className="text-xs text-zinc-500 mt-1">
          Elige <strong>por dónde</strong> recibir tus avisos. La campanita
          dentro de la app siempre los recibe; los demás canales los prendes o
          apagas a tu gusto.
        </p>
      </div>

      {/* --- Canales --- */}
      {prefs && (
        <div className="space-y-3">
          <CanalRow
            icon="🔔"
            titulo="En la app (campanita)"
            desc="Todo aviso llega aquí. No se puede apagar."
            control={
              <span className="px-2 py-0.5 rounded border text-[10px] uppercase tracking-wide bg-zinc-500/10 text-zinc-400 border-zinc-500/30">
                🔒 Siempre
              </span>
            }
          />
          <CanalRow
            icon="📱"
            titulo="Navegador (push)"
            desc="Avisos en tu pantalla aunque la app esté cerrada. Apagado: tus dispositivos siguen dados de alta, pero no les llega nada."
            control={
              <Toggle
                checked={prefs.notif_push}
                onChange={(v) =>
                  void cambiarCanal(
                    "notif_push",
                    v,
                    "Avisos del navegador",
                    "Tus dispositivos siguen dados de alta; solo dejan de sonar.",
                  )
                }
              >
                <span className="sr-only">Navegador (push)</span>
              </Toggle>
            }
          />
          <CanalRow
            icon="💬"
            titulo="Telegram"
            desc={
              prefs.telegram_vinculado
                ? "Avisos al chat de tu bot de Telegram. Apagado: sigues vinculado, pero no te llega nada."
                : "Primero vincula Telegram en la sección de abajo para poder activarlo."
            }
            control={
              <Toggle
                checked={prefs.notif_telegram}
                disabled={!prefs.telegram_vinculado}
                onChange={(v) => void cambiarCanal("notif_telegram", v, "Telegram")}
              >
                <span className="sr-only">Telegram</span>
              </Toggle>
            }
          />
          <CanalRow
            icon="✉️"
            titulo="Correo"
            desc="Recordatorios, resumen diario y pases de caso, a tu correo. Apagado: no te llega nada de eso por correo."
            control={
              <Toggle
                checked={prefs.notif_email}
                onChange={(v) =>
                  void cambiarCanal(
                    "notif_email",
                    v,
                    "Avisos por correo",
                    "No te llegarán recordatorios, resumen ni pases de caso por correo.",
                  )
                }
              >
                <span className="sr-only">Correo</span>
              </Toggle>
            }
          />
        </div>
      )}

      {/* --- Ruteo por nivel de urgencia --- */}
      {prefs && (
        <div className="border-t border-ink-700 pt-3 space-y-3">
          <div>
            <h3 className="text-xs font-medium text-zinc-300">
              Ruteo por nivel de urgencia
            </h3>
            <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">
              Qué tan fuerte suena cada aviso. La campanita siempre recibe
              todo; aquí decides si además vibra tu navegador o tu Telegram.
              El correo no entra en este ruteo — hoy solo aplica a
              recordatorios, resumen diario y pases de caso (arriba).
            </p>
          </div>
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-2.5 items-center">
            <span aria-hidden />
            <span className="text-[10px] uppercase tracking-wide text-zinc-500 text-center">
              📱 Push
            </span>
            <span className="text-[10px] uppercase tracking-wide text-zinc-500 text-center">
              💬 Telegram
            </span>
            {ORDEN_URGENCIA.map((nivel) => {
              const canales = canalesDeNivel(nivel, prefs.notif_nivel_canales);
              return (
                <Fragment key={nivel}>
                  <span className="text-sm text-zinc-200">
                    {URGENCIA_META[nivel].titulo}
                  </span>
                  <Toggle
                    checked={canales.includes("push")}
                    onChange={(v) => void cambiarNivelCanal(nivel, "push", v)}
                    className="justify-self-center"
                  >
                    <span className="sr-only">{`${URGENCIA_META[nivel].titulo}: navegador`}</span>
                  </Toggle>
                  <Toggle
                    checked={canales.includes("telegram")}
                    disabled={!prefs.telegram_vinculado}
                    onChange={(v) => void cambiarNivelCanal(nivel, "telegram", v)}
                    className="justify-self-center"
                  >
                    <span className="sr-only">{`${URGENCIA_META[nivel].titulo}: Telegram`}</span>
                  </Toggle>
                </Fragment>
              );
            })}
          </div>
          {!prefs.telegram_vinculado && (
            <p className="text-[11px] text-zinc-500">
              La columna de Telegram sale gris: primero vincula Telegram en la sección de abajo.
            </p>
          )}

          {/* --- Urgencia por tipo (avanzado, colapsable) --- */}
          <div className="border-t border-ink-700 pt-3">
            <button
              type="button"
              onClick={() => setTipoAbierto((v) => !v)}
              className="text-xs uppercase tracking-wide text-zinc-500 hover:text-zinc-300 transition flex items-center gap-1.5"
            >
              <span>{tipoAbierto ? "▾" : "▸"}</span>
              Urgencia por tipo de aviso
              <span className="normal-case tracking-normal text-zinc-600">
                (avanzado)
              </span>
            </button>

            {tipoAbierto && (
              <div className="mt-3 space-y-2.5">
                <p className="text-[11px] text-zinc-500 leading-snug">
                  Cada tipo de aviso trae un nivel por default; cámbialo si
                  quieres que alguno suene distinto para ti.
                </p>
                {Object.entries(TIPO_META).map(([tipo, meta]) => {
                  const nivel = nivelDeTipo(tipo, prefs.notif_tipo_urgencia);
                  return (
                    <div
                      key={tipo}
                      className="flex items-center justify-between gap-2"
                    >
                      <span
                        className={`flex items-center gap-1.5 text-sm min-w-0 ${meta.color}`}
                      >
                        <span aria-hidden>{meta.icono}</span>
                        <span className="truncate">{meta.label}</span>
                      </span>
                      <SelectorNivel
                        value={nivel}
                        onChange={(n) => void cambiarTipoUrgencia(tipo, n)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- Dispositivos del push de ESTE navegador --- */}
      <div className="border-t border-ink-700 pt-3 space-y-2">
        <p className="text-xs text-zinc-400">
          Dispositivos para el push de este navegador
        </p>
        {!pushSoportado ? (
          <p className="text-xs text-zinc-500">
            Este navegador no soporta notificaciones push. En iPhone, primero
            agrega la app a tu pantalla de inicio (Compartir → Agregar a pantalla
            de inicio) y ábrela desde ahí.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wide">
              <span className={`px-2 py-0.5 rounded border ${permisoCls}`}>
                permiso: {permisoLabel}
              </span>
              {prefs && !prefs.notif_push && (
                <span className="px-2 py-0.5 rounded border bg-amber-500/10 text-amber-300 border-amber-500/30">
                  silenciado
                </span>
              )}
              <span
                className={`px-2 py-0.5 rounded border ${
                  suscrito
                    ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                    : "bg-zinc-500/10 text-zinc-400 border-zinc-500/30"
                }`}
              >
                este dispositivo: {suscrito ? "suscrito" : "sin suscribir"}
              </span>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                onClick={() => void activar()}
                disabled={activando}
                className="btn-primary text-sm w-full sm:w-auto disabled:opacity-50"
              >
                {activando
                  ? "Activando…"
                  : suscrito
                    ? "Reactivar en este dispositivo"
                    : "Activar en este dispositivo"}
              </button>
              <button
                onClick={() => void probar()}
                disabled={probando}
                className="btn-ghost text-sm w-full sm:w-auto disabled:opacity-50"
              >
                {probando ? "Enviando…" : "Enviar prueba a mis dispositivos"}
              </button>
            </div>

            <MisEquipos reloadKey={reloadEquipos} />
          </>
        )}
      </div>

      {/* --- Horario de silencio --- */}
      {prefs && (
        <div className="border-t border-ink-700 pt-3 space-y-2">
          <Toggle
            checked={prefs.notif_silencio}
            onChange={(v) => void cambiarPrefs({ notif_silencio: v })}
            label="Horario de silencio"
            hint="Todos los días, entre esas dos horas no suenan el navegador ni Telegram."
          />
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center text-xs text-zinc-400">
            <label className="flex items-center gap-2">
              <span>de las</span>
              <input
                type="time"
                disabled={!prefs.notif_silencio}
                {...silencioInicio}
                className="input w-36 disabled:opacity-50"
              />
            </label>
            <label className="flex items-center gap-2">
              <span>a las</span>
              <input
                type="time"
                disabled={!prefs.notif_silencio}
                {...silencioFin}
                className="input w-36 disabled:opacity-50"
              />
            </label>
          </div>
          <p className="text-xs text-zinc-500">
            Lo que pase en ese horario queda guardado en la campanita y al
            terminar te llega un solo resumen. Los recordatorios que tú
            programes sí suenan, como las alarmas del celular.
          </p>
          <div className="border-t border-ink-700 pt-3 space-y-2">
            <Toggle
              checked={prefs.silencio_finde}
              onChange={(v) => void cambiarPrefs({ silencio_finde: v })}
              label="Silenciar fines de semana y festivos"
              hint="Sábados, domingos y festivos de México no suenan el navegador ni Telegram; todo queda en la campanita. Tus recordatorios sí suenan."
            />
            <p className="text-xs text-zinc-500">
              Viene <strong>activado</strong>. Si trabajas en fin de semana o
              quieres recibir avisos en días festivos, apágalo. El lunes (o el
              primer día hábil) te llega un resumen de lo que se acumuló.
            </p>
          </div>
        </div>
      )}

      {/* --- Modo enfoque --- */}
      {prefs && (
        <div className="border-t border-ink-700 pt-3 space-y-2">
          <CanalRow
            icon="🎯"
            titulo="Modo enfoque"
            desc={`Silencia los avisos del navegador y de Telegram un rato. ${ENFOQUE_TEXTOS.detalle}`}
            control={
              enfoqueActivo && prefs.enfoque_hasta ? (
                <span className="px-2 py-0.5 rounded border text-[10px] uppercase tracking-wide bg-accent-500/10 text-accent-300 border-accent-500/40">
                  🎯 hasta {formatHoraEnfoque(prefs.enfoque_hasta)}
                </span>
              ) : (
                <span className="px-2 py-0.5 rounded border text-[10px] uppercase tracking-wide bg-zinc-500/10 text-zinc-400 border-zinc-500/30">
                  apagado
                </span>
              )
            }
          />
          <div className="flex flex-wrap gap-2">
            {enfoqueActivo ? (
              <button
                type="button"
                onClick={() => void terminarEnf()}
                disabled={enfoqueCargando}
                className="btn-ghost text-sm disabled:opacity-50"
              >
                Terminar
              </button>
            ) : (
              ENFOQUE_OPCIONES.map((o) => (
                <button
                  key={o.minutos}
                  type="button"
                  onClick={() => void iniciarEnf(o.minutos)}
                  disabled={enfoqueCargando}
                  className="btn-ghost text-sm disabled:opacity-50"
                >
                  {o.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
